import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AppDispatch, RootState } from '../store/store';
import { fetchClaims, getFacilities, getLossTypes } from '../services/claimsApi';
import { LossType } from '../store/claimsSlice';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PageControls from '../components/PageControls';
import {
  formatDateOnlyDisplay,
  formatDateOnlyIso,
  formatFiledDateDisplay,
  parseDateOnlyLocal,
} from '../utils/dateOnly';
import { getStatusChipSx } from '../utils/claimStatusStyles';

const CLAIMS_LIST_STATE_KEY = 'claimsListState_v1';

type ClaimsListPersistedState = {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  facilityLocation?: string[];
  claimStatus?: string[];
  lossType?: string[];
  dateFiledStart?: string | null;
  dateFiledEnd?: string | null;
};

const safeJsonParse = <T,>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const ClaimsList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { claims = [], loading, error, total = 0 } = useSelector((state: RootState) => state.claims);
  const syncUrlOnFirstRender = useRef(false);

  const persistedState = (() => {
    const hasUrlState =
      searchParams.has('page') ||
      searchParams.has('pageSize') ||
      searchParams.has('sortBy') ||
      searchParams.has('sortOrder') ||
      searchParams.has('search') ||
      searchParams.has('facilityLocation') ||
      searchParams.has('claimStatus') ||
      searchParams.has('lossType') ||
      searchParams.has('dateFiledStart') ||
      searchParams.has('dateFiledEnd');

    if (hasUrlState) {
      return null;
    }

    const stored = safeJsonParse<ClaimsListPersistedState>(localStorage.getItem(CLAIMS_LIST_STATE_KEY));
    if (stored) {
      // If we restore from localStorage and there are no URL params, we want the URL to reflect it.
      syncUrlOnFirstRender.current = true;
    }
    return stored;
  })();
  
  // Initialize state from URL params if available, otherwise use defaults
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : (persistedState?.page ?? 1);
  });
  const [pageSize, setPageSize] = useState(() => {
    const pageSizeParam = searchParams.get('pageSize');
    return pageSizeParam ? parseInt(pageSizeParam, 10) : (persistedState?.pageSize ?? 100);
  });
  const [sortBy, setSortBy] = useState<string>(() => {
    return searchParams.get('sortBy') || persistedState?.sortBy || 'created_at';
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    const sortOrderParam = searchParams.get('sortOrder');
    return (sortOrderParam === 'asc' || sortOrderParam === 'desc')
      ? sortOrderParam
      : (persistedState?.sortOrder ?? 'desc');
  });
  
  // Initialize filter state from URL params
  const [searchQuery, setSearchQuery] = useState(() => {
    return searchParams.get('search') || persistedState?.search || '';
  });
  // Submitted search query - only this triggers API calls
  const [submittedSearchQuery, setSubmittedSearchQuery] = useState(() => {
    return searchParams.get('search') || persistedState?.search || '';
  });
  const [facilityLocation, setFacilityLocation] = useState<string[]>(() => {
    const values = searchParams.getAll('facilityLocation');
    return values.length ? values : (persistedState?.facilityLocation ?? []);
  });
  const [claimStatus, setClaimStatus] = useState<string[]>(() => {
    const values = searchParams.getAll('claimStatus');
    return values.length ? values : (persistedState?.claimStatus ?? []);
  });
  const [lossType, setLossType] = useState<string[]>(() => {
    const values = searchParams.getAll('lossType');
    return values.length ? values : (persistedState?.lossType ?? []);
  });
  const [dateFiledStart, setDateFiledStart] = useState<Date | null>(() => {
    const dateParam = searchParams.get('dateFiledStart');
    const fallback = persistedState?.dateFiledStart
      ? parseDateOnlyLocal(persistedState.dateFiledStart)
      : null;
    return dateParam ? parseDateOnlyLocal(dateParam) : fallback;
  });
  const [dateFiledEnd, setDateFiledEnd] = useState<Date | null>(() => {
    const dateParam = searchParams.get('dateFiledEnd');
    const fallback = persistedState?.dateFiledEnd
      ? parseDateOnlyLocal(persistedState.dateFiledEnd)
      : null;
    return dateParam ? parseDateOnlyLocal(dateParam) : fallback;
  });
  const [facilityLocations, setFacilityLocations] = useState<Array<{id: number, location_code: string}>>([]);
  const [lossTypes, setLossTypes] = useState<LossType[]>([]);
  const isInitialMount = useRef(true);
  const facilityLocationRef = useRef(facilityLocation);
  facilityLocationRef.current = facilityLocation;

  // Mock data for dropdowns - replace with actual data from your API
  const claimStatuses = ['SUBMITTED', 'IN_REVIEW', 'PENDING_REVIEW', 'INCOMPLETE', 'APPROVED', 'DENIED', 'DRAFT', 'LEGAL_MEDIATION', 'SITE_CLOSED', 'WITH_OPERATIONS', 'PENDING_SIGNATURE', 'PAID', 'CLOSED', 'DUPLICATE'];
  
  // Map status values to display labels
  const statusLabels: Record<string, string> = {
    'SUBMITTED': 'Submitted',
    'IN_REVIEW': 'In Review',
    'PENDING_REVIEW': 'Pending Review',
    'INCOMPLETE': 'Incomplete',
    'APPROVED': 'Approved',
    'DENIED': 'Denied',
    'DRAFT': 'Draft',
    'LEGAL_MEDIATION': 'Legal Mediation',
    'SITE_CLOSED': 'Site Closed',
    'WITH_OPERATIONS': 'With Operations',
    'PENDING_SIGNATURE': 'Pending Signature',
    'PAID': 'Paid',
    'CLOSED': 'Closed',
    'DUPLICATE': 'Duplicate'
  };

  // Helper function to build filters object from advanced search state
  const buildFilters = useCallback((): Record<string, string | string[]> => {
    const filters: Record<string, string | string[]> = {};
    
    // Facility filter stores location_code values (e.g. "1001", "1002")
    if (facilityLocation.length) {
      // API expects a comma-separated list for facility/location codes
      filters['location_code'] = facilityLocation.join(',');
    }
    
    if (claimStatus.length) {
      // API expects a comma-separated list for status, e.g. "SUBMITTED,DENIED"
      filters['status'] = claimStatus.join(',');
    }

    if (lossType.length) {
      // API expects a comma-separated list for loss types
      filters['tpp_loss_type_id'] = lossType.join(',');
    }
    
    if (dateFiledStart) {
      filters['created_at_start'] = formatDateOnlyIso(dateFiledStart);
    }

    if (dateFiledEnd) {
      filters['created_at_end'] = formatDateOnlyIso(dateFiledEnd);
    }
    
    return filters;
  }, [facilityLocation, claimStatus, lossType, dateFiledStart, dateFiledEnd]);

  // Update URL params when state changes (but not on initial mount to avoid overriding URL params)
  useEffect(() => {
    // Skip URL update on initial mount - state was already initialized from URL params
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!syncUrlOnFirstRender.current) {
        return;
      }
    }
    
    // Build expected params from current state
    const expectedParams = new URLSearchParams();
    if (page !== 1) expectedParams.set('page', page.toString());
    if (pageSize !== 100) expectedParams.set('pageSize', pageSize.toString());
    if (sortBy !== 'created_at') expectedParams.set('sortBy', sortBy);
    if (sortOrder !== 'desc') expectedParams.set('sortOrder', sortOrder);
    if (submittedSearchQuery) expectedParams.set('search', submittedSearchQuery);
    facilityLocation.forEach((id) => {
      expectedParams.append('facilityLocation', id);
    });
    claimStatus.forEach((status) => {
      expectedParams.append('claimStatus', status);
    });
    lossType.forEach((lt) => {
      expectedParams.append('lossType', lt);
    });
    if (dateFiledStart) {
      expectedParams.set('dateFiledStart', formatDateOnlyIso(dateFiledStart));
    }
    if (dateFiledEnd) {
      expectedParams.set('dateFiledEnd', formatDateOnlyIso(dateFiledEnd));
    }
    
    setSearchParams(expectedParams, { replace: true });
  }, [page, pageSize, sortBy, sortOrder, submittedSearchQuery, facilityLocation, claimStatus, lossType, dateFiledStart, dateFiledEnd, setSearchParams]);

  // Persist list state so filter selections survive navigation until Reset.
  useEffect(() => {
    const stateToPersist: ClaimsListPersistedState = {
      page,
      pageSize,
      sortBy,
      sortOrder,
      search: submittedSearchQuery || '',
      facilityLocation,
      claimStatus,
      lossType,
      dateFiledStart: dateFiledStart ? formatDateOnlyIso(dateFiledStart) : null,
      dateFiledEnd: dateFiledEnd ? formatDateOnlyIso(dateFiledEnd) : null,
    };

    localStorage.setItem(CLAIMS_LIST_STATE_KEY, JSON.stringify(stateToPersist));
  }, [page, pageSize, sortBy, sortOrder, submittedSearchQuery, facilityLocation, claimStatus, lossType, dateFiledStart, dateFiledEnd]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [facilities, lossTypesData] = await Promise.all([
          getFacilities(),
          getLossTypes()
        ]);
        const mappedFacilities = facilities.map((facility: any) => ({
          id: facility.id,
          location_code: facility.location_code
        }));
        setFacilityLocations(mappedFacilities);
        setLossTypes(lossTypesData);

        // Back-compat: if facilityLocation contains old facility IDs, convert to location_code
        // once facilities are loaded so the multi-select displays correctly.
        const currentFacilityLocation = facilityLocationRef.current;
        if (currentFacilityLocation.length) {
          const converted = currentFacilityLocation.map((v) => {
            const match = mappedFacilities.find((f: any) => f.id.toString() === v);
            return match ? match.location_code : v;
          });
          const changed =
            converted.length !== currentFacilityLocation.length ||
            converted.some((v, i) => v !== currentFacilityLocation[i]);
          if (changed) {
            setFacilityLocation(converted);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Fetch claims when dependencies change (including when facilities/lossTypes are loaded)
  useEffect(() => {
    // Use current state values (which may have been restored from URL)
    const filters = buildFilters();
    dispatch(fetchClaims({ page, pageSize, sortBy, sortOrder, search: submittedSearchQuery, filters }));
  }, [dispatch, page, pageSize, sortBy, sortOrder, submittedSearchQuery, buildFilters]);


  // Handler for filter changes.
  // We only update state here; the fetch useEffect runs once with fresh state.
  const handleFilterChange = () => {
    setPage(1); // Reset to first page when filter changes
  };

  const handleFacilityLocationChange = (values: string[]) => {
    setFacilityLocation(values);
    handleFilterChange();
  };

  const handleClaimStatusChange = (values: string[]) => {
    setClaimStatus(values);
    handleFilterChange();
  };

  const handleLossTypeChange = (values: string[]) => {
    setLossType(values);
    handleFilterChange();
  };

  const handleDateFiledStartChange = (newValue: Date | null) => {
    setDateFiledStart(newValue);
    handleFilterChange();
  };

  const handleDateFiledEndChange = (newValue: Date | null) => {
    setDateFiledEnd(newValue);
    handleFilterChange();
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    const filters = buildFilters();
    dispatch(fetchClaims({ page: value, search: submittedSearchQuery, pageSize, sortBy, sortOrder, filters }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page when searching
    setSubmittedSearchQuery(searchQuery); // Update submitted search query
    const filters = buildFilters();
    dispatch(fetchClaims({ search: searchQuery, page: 1, pageSize, sortBy, sortOrder, filters }));
  };

  const handleReset = () => {
    // Reset all search and filter state
    setSearchQuery('');
    setSubmittedSearchQuery('');
    setFacilityLocation([]);
    setClaimStatus([]);
    setLossType([]);
    setDateFiledStart(null);
    setDateFiledEnd(null);
    setPage(1);
    setPageSize(100);
    setSortBy('created_at');
    setSortOrder('desc');
    
    // Clear URL params
    setSearchParams(new URLSearchParams(), { replace: true });

    // Clear persisted list state
    localStorage.removeItem(CLAIMS_LIST_STATE_KEY);
    
    // Reload claims with default parameters
    dispatch(fetchClaims({ page: 1, pageSize: 100, sortBy: 'created_at', sortOrder: 'desc' }));
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newPageSize = Number(event.target.value);
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
    const filters = buildFilters();
    dispatch(fetchClaims({ page: 1, search: submittedSearchQuery, pageSize: newPageSize, sortBy, sortOrder, filters }));
  };

  // Column to API field mapping
  const columnToSortField: Record<string, string> = {
    'Facility Location': 'location_code',
    'First Name': 'reporter_first_name',
    'Last Name': 'reporter_last_name',
    'Claim Number': 'claim_number',
    'Amount': 'damage_estimate',
    'Incident Date': 'incident_date',
    'Filed Date': 'created_at',
    'Loss Type': 'tpp_loss_type_id',
    'Status': 'status',
  };

  const handleSort = (column: string) => {
    const field = columnToSortField[column];
    if (!field) return; // Don't sort if column doesn't have a mapping (e.g., Actions)

    const filters = buildFilters();

    if (sortBy === field) {
      // Toggle sort order if clicking the same column
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newOrder);
      setPage(1); // Reset to first page when sorting changes
      dispatch(fetchClaims({ page: 1, search: submittedSearchQuery, pageSize, sortBy: field, sortOrder: newOrder, filters }));
    } else {
      // Set new sort column with default desc order
      setSortBy(field);
      setSortOrder('desc');
      setPage(1); // Reset to first page when sorting changes
      dispatch(fetchClaims({ page: 1, search: submittedSearchQuery, pageSize, sortBy: field, sortOrder: 'desc', filters }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUBMITTED':
        return 'warning';
      case 'IN_REVIEW':
      case 'PENDING_REVIEW':
        return 'info';
      case 'INCOMPLETE':
        return 'default';
      case 'APPROVED':
        return 'success';
      case 'DENIED':
        return 'error';
      case 'LEGAL_MEDIATION':
      case 'PENDING_SIGNATURE':
        return 'warning';
      case 'PAID':
      case 'SITE_CLOSED':
        return 'success';
      case 'CLOSED':
        return 'default';
      case 'DUPLICATE':
        return 'default';
      case 'WITH_OPERATIONS':
        return 'info';
      default:
        return 'default';
    }
  };

  if (error) {
    return (
      <Container>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ width: '100%', px: 2 }}>
      <Box sx={{ my: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography variant="h4" component="h1">
            Claims
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            flexWrap: 'wrap',
            flex: 1,
            justifyContent: 'flex-end'
          }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Facility</InputLabel>
              <Select
                multiple
                value={facilityLocation as any}
                label="Facility"
                renderValue={(selected) =>
                  (selected as string[]).length ? (selected as string[]).join(', ') : 'All'
                }
                onChange={(e: SelectChangeEvent) => {
                  const value = e.target.value as unknown as string[];
                  handleFacilityLocationChange(value);
                }}
              >
                <MenuItem value="">
                  <Checkbox checked={facilityLocation.length === 0} />
                  <ListItemText primary="All" />
                </MenuItem>
                {facilityLocations.map((facility) => {
                  const code = facility.location_code;
                  const selected = facilityLocation.includes(code);
                  return (
                    <MenuItem key={facility.id} value={code}>
                      <Checkbox checked={selected} />
                      <ListItemText primary={facility.location_code} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={claimStatus as any}
                label="Claim Status"
                renderValue={(selected) =>
                  (selected as string[]).filter(Boolean).length
                    ? (selected as string[]).filter(Boolean).map((s) => statusLabels[s] || s).join(', ')
                    : 'All'
                }
                onChange={(e: SelectChangeEvent) => {
                  const raw = e.target.value as unknown as string[];
                  // If the "All" option (empty string) is selected, clear selections.
                  const value = raw.includes('') ? [] : raw.filter(Boolean);
                  handleClaimStatusChange(value);
                }}
              >
                <MenuItem value="">
                  <Checkbox checked={claimStatus.length === 0} />
                  <ListItemText primary="All" />
                </MenuItem>
                {claimStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={claimStatus.includes(status)} />
                    <ListItemText primary={statusLabels[status] || status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Loss Type</InputLabel>
              <Select
                multiple
                value={lossType as any}
                label="Loss Type"
                renderValue={(selected) =>
                  (selected as string[]).filter(Boolean).length
                    ? lossTypes
                        .filter((t) => (selected as string[]).filter(Boolean).includes(String(t.id)))
                        .map((t) => t.name)
                        .join(', ')
                    : 'All'
                }
                onChange={(e: SelectChangeEvent) => {
                  const raw = e.target.value as unknown as string[];
                  const value = raw.includes('') ? [] : raw.filter(Boolean);
                  handleLossTypeChange(value);
                }}
              >
                <MenuItem value="">
                  <Checkbox checked={lossType.length === 0} />
                  <ListItemText primary="All" />
                </MenuItem>
                {lossTypes.map((type) => (
                  <MenuItem key={type.id} value={String(type.id)}>
                    <Checkbox checked={lossType.includes(String(type.id))} />
                    <ListItemText primary={type.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Filed Date Start"
                value={dateFiledStart}
                onChange={handleDateFiledStartChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { 
                      minWidth: 220, 
                      maxWidth: 220,
                    },
                  },
                }}
              />
              <DatePicker
                label="Filed Date End"
                value={dateFiledEnd}
                onChange={handleDateFiledEndChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { 
                      minWidth: 220, 
                      maxWidth: 220,
                    },
                  },
                }}
              />
            </LocalizationProvider>

            <Box component="form" onSubmit={handleSearch} sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2 
            }}>
              <TextField
                size="small"
                placeholder="Search claims..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button type="submit" size="small">
                        <SearchIcon />
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Typography
              component="a"
              onClick={(e) => {
                e.preventDefault();
                handleReset();
              }}
              sx={{ 
                fontSize: '0.875rem',
                color: 'primary.main',
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                  color: 'primary.dark',
                }
              }}
            >
              Reset
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <PageControls
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </Box>

        <Box sx={{ position: 'relative' }}>
          <TableContainer component={Paper} sx={{ width: '100%' }}>
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell 
                    onClick={() => handleSort('Facility')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Facility
                      {sortBy === 'location_code' && (
                        sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('First Name')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      First Name
                      {sortBy === 'reporter_first_name' && (
                        sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('Last Name')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Last Name
                      {sortBy === 'reporter_last_name' && (
                        sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('Claim Number')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Claim Number
                      {sortBy === 'claim_number' && (
                        sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('Amount')}
                    sx={{ cursor: 'pointer', userSelect: 'none', textAlign: 'right' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      Amount
                      {sortBy === 'damage_estimate' && (
                        sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('Incident Date')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Incident Date
                      {sortBy === 'incident_date' && (
                        sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('Filed Date')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Filed Date
                      {sortBy === 'created_at' && (
                        sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('Loss Type')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Loss Type
                      {sortBy === 'tpp_loss_type_id' && (
                        sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    onClick={() => handleSort('Status')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Status
                      {sortBy === 'status' && (
                        sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(claims) && claims.map((claim) => (
                  <TableRow key={claim.id}>
                    <TableCell>{claim.facility?.location_code || 'N/A'}</TableCell>
                    <TableCell>{claim.reporter_first_name} </TableCell>
                    <TableCell>{claim.reporter_last_name}</TableCell>
                    <TableCell>{claim.claim_number}</TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>${claim.damage_estimate}</TableCell>
                    <TableCell>
                      {formatDateOnlyDisplay(claim.incident_date)}
                    </TableCell>
                    <TableCell>
                      {formatFiledDateDisplay(claim.created_at)}
                    </TableCell>
                    <TableCell>
                      {claim.loss_type?.name || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={claim.status}
                        color={getStatusColor(claim.status) as any}
                        size="small"
                        sx={getStatusChipSx(claim.status)}
                      />
                    </TableCell>  
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                          // Build query params with current list state and filters
                          const params = new URLSearchParams();
                          if (page !== 1) params.set('page', page.toString());
                          if (pageSize !== 100) params.set('pageSize', pageSize.toString());
                          if (sortBy !== 'created_at') params.set('sortBy', sortBy);
                          if (sortOrder !== 'desc') params.set('sortOrder', sortOrder);
                          if (submittedSearchQuery) params.set('search', submittedSearchQuery);
                          facilityLocation.forEach((id) => params.append('facilityLocation', id));
                          claimStatus.forEach((status) => params.append('claimStatus', status));
                          lossType.forEach((lt) => params.append('lossType', lt));
                          if (dateFiledStart) {
                            params.set('dateFiledStart', formatDateOnlyIso(dateFiledStart));
                          }
                          if (dateFiledEnd) {
                            params.set('dateFiledEnd', formatDateOnlyIso(dateFiledEnd));
                          }
                          const queryString = params.toString();
                          navigate(`/claims/${claim.id}${queryString ? `?${queryString}` : ''}`);
                        }}
                        sx={{
                          fontSize: '1.1rem',
                          padding: '6px 16px',
                          textTransform: 'none',
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {loading && claims.length === 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {loading && claims.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
        
        <Box sx={{ mt: 2 }}>
          <PageControls
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default ClaimsList; 